import { Link } from "react-router-dom";
import {
  Phone,
  Clock,
  ShieldCheck,
  BadgeEuro,
  Star,
  Check,
  MapPin,
  ArrowRight,
  KeyRound,
  DoorOpen,
  ShieldAlert,
  Shield,
  Mailbox,
  BadgeCheck,
} from "lucide-react";
import {
  COMPANY,
  HERO_IMAGE,
  SERVICES,
  INTERVENTION_CITIES,
  TRUCK_IMAGE,
} from "../lib/constants";
import useSeo, { SITE } from "../lib/useSeo";
import BrandMarquee from "../components/BrandMarquee";
import BrandsSection from "../components/BrandsSection";
import InterventionsGallery from "../components/InterventionsGallery";
import ArtisanSection from "../components/ArtisanSection";
import InsurancePartners from "../components/InsurancePartners";
import Reviews from "../components/Reviews";

const ICON_MAP = { KeyRound, DoorOpen, ShieldAlert, Shield, Mailbox, BadgeCheck };

export default function Home() {
  useSeo({
    title:
      "Serrurier Mont-Dol 35120 — Dépannage urgence 24h/24 en 30 min | Mon Serrurier Breton",
    description:
      "Artisan serrurier à Mont-Dol (Ille-et-Vilaine). Dépannage urgence 24h/24, ouverture de porte sans casse, changement de serrure, blindage. Intervention en 30 min sur Dol-de-Bretagne, Saint-Malo, Dinard, Cancale, Dinan et tout le 22-35. Devis gratuit · 4,9/5 sur Google.",
    path: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${SITE.url}/#service`,
        name: "Serrurerie & dépannage 24h/24 — Mon Serrurier Breton",
        serviceType: "Locksmith",
        provider: { "@id": `${SITE.url}/#business` },
        areaServed: [
          { "@type": "AdministrativeArea", name: "Côtes-d'Armor (22)" },
          { "@type": "AdministrativeArea", name: "Ille-et-Vilaine (35)" },
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Prestations serrurerie",
          itemListElement: [
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Ouverture de porte sans casse 24h/24" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Changement de serrure et de cylindre" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Mise en sécurité après effraction" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Blindage de porte" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Cylindre haute sécurité A2P" } },
            { "@type": "Offer", itemOffered: { "@type": "Service", name: "Serrurerie boîte aux lettres" } },
          ],
        },
      },
    ],
  });
  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top_right,_rgba(0,156,156,0.12),transparent_55%)]" />
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-7 fade-rise">
            <div
              data-testid="hero-badge"
              className="inline-flex items-center gap-2 bg-orange-100 text-orange-700 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest"
            >
              <span className="w-2 h-2 rounded-full bg-orange-600 animate-pulse" />
              Urgence — Dépannage 24h/24
            </div>
            <h1
              data-testid="hero-title"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-slate-900 leading-[0.95]"
            >
              Un serrurier <span className="text-teal-600">professionnel</span>{" "}
              et diplômé <br />
              chez vous en{" "}
              <span className="relative inline-block">
                <span className="relative z-10">30 minutes</span>
                <span className="absolute bottom-1 left-0 right-0 h-3 bg-amber-300/70 -z-0" />
              </span>
              .
            </h1>
            <p className="text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
              Serrurier basé à <strong>Mont-Dol</strong>, tarifs honnêtes,
              travail de qualité garanti. Ouverture de porte, changement de
              serrure, mise en sécurité après effraction.{" "}
              <strong>Devis gratuit</strong> — Remboursement assurance sur
              facture.
            </p>

            <div className="flex flex-wrap items-center gap-3">
              <a
                data-testid="hero-cta-call"
                href={`tel:${COMPANY.phoneDigits}`}
                className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white font-bold rounded-full px-7 py-4 shadow-xl shadow-orange-600/30 transition-all text-lg pulse-emergency"
              >
                <Phone className="w-5 h-5" />
                {COMPANY.phone}
              </a>
              <Link
                data-testid="hero-cta-services"
                to="/services"
                className="inline-flex items-center gap-2 bg-white border-2 border-slate-900 text-slate-900 font-bold rounded-full px-6 py-4 hover:bg-slate-900 hover:text-white transition-all"
              >
                Nos services <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div
              data-testid="hero-trust-row"
              className="flex flex-wrap items-center gap-x-6 gap-y-3 pt-4 border-t border-slate-200"
            >
              <div className="flex items-center gap-1.5">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-4 h-4 fill-amber-400 text-amber-400"
                  />
                ))}
                <span className="ml-1 font-bold text-slate-900">
                  {COMPANY.googleRating}/5
                </span>
                <span className="text-xs text-slate-500">
                  sur {COMPANY.googleReviews} avis Google
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Check className="w-4 h-4 text-teal-600" />
                Devis gratuit
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Check className="w-4 h-4 text-teal-600" />
                Artisan diplômé
              </div>
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Check className="w-4 h-4 text-teal-600" />
                Assurance acceptée
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div
              data-testid="hero-image-wrap"
              className="relative rounded-[2rem] overflow-hidden border border-slate-200 shadow-2xl shadow-teal-900/10 bg-white"
            >
              <img
                src={HERO_IMAGE}
                alt="Serrurier en intervention : perçage de cylindre sur porte blindée"
                className="w-full h-[460px] md:h-[520px] object-cover"
                style={{ objectPosition: "40% 50%" }}
              />
              <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-teal-600 text-white flex items-center justify-center">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs text-slate-500 font-semibold">
                    Intervention moyenne
                  </div>
                  <div className="font-display font-extrabold text-slate-900">
                    30 minutes
                  </div>
                </div>
              </div>
              <div className="absolute bottom-4 right-4 bg-slate-900 text-white rounded-2xl px-4 py-3 shadow-lg flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-400 text-slate-900 flex items-center justify-center font-black">
                  +
                </div>
                <div>
                  <div className="text-xs text-slate-400 font-semibold uppercase tracking-wider">
                    Interventions
                  </div>
                  <div className="font-display font-extrabold text-2xl leading-none">
                    {COMPANY.interventions}+
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Key selling points row */}
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-10">
          <div
            data-testid="hero-points-row"
            className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4"
          >
            {[
              { icon: Clock, label: "24h/24 — 7j/7" },
              { icon: BadgeEuro, label: "Devis gratuit" },
              { icon: ShieldCheck, label: "Travail garanti" },
              { icon: Check, label: "Assurance acceptée" },
            ].map((it, i) => (
              <div
                key={i}
                className="flex items-center gap-3 bg-white border border-slate-200 rounded-2xl px-4 py-3"
              >
                <it.icon className="w-5 h-5 text-teal-600 flex-shrink-0" />
                <span className="text-sm font-semibold text-slate-800">
                  {it.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Rating strip */}
      <section
        data-testid="google-rating-strip"
        className="max-w-7xl mx-auto px-4 md:px-8"
      >
        <div className="rounded-3xl bg-slate-900 text-white overflow-hidden relative">
          <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-teal-600/20 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 p-10 md:p-12">
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-teal-300 mb-2">
                Notation Google
              </div>
              <div className="font-display text-4xl md:text-5xl font-extrabold">
                {COMPANY.googleRating}/5
              </div>
              <div className="flex items-center gap-1 mt-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className="w-5 h-5 fill-amber-400 text-amber-400"
                  />
                ))}
                <span className="ml-2 text-sm text-slate-300">
                  {COMPANY.googleReviews} avis
                </span>
              </div>
            </div>
            <div className="md:col-span-2 flex flex-col justify-center">
              <h2 className="font-display text-2xl md:text-3xl font-bold leading-snug">
                Besoin d'un serrurier fiable, réactif et transparent ?
              </h2>
              <p className="text-slate-300 mt-3 leading-relaxed">
                Mon Serrurier Breton accompagne particuliers et professionnels
                avec un service de qualité : ouverture de porte, remplacement de
                serrure, sécurisation après effraction, blindage et
                interventions rapides en toute confiance.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* USP Bento */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="max-w-2xl mb-12">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
            <span className="h-px w-8 bg-teal-700" /> Pourquoi nous choisir
          </div>
          <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
            Un artisan serrurier breton,
            <br />
            <span className="text-teal-600">proche, rapide et honnête.</span>
          </h2>
        </div>

        <div
          data-testid="usp-bento"
          className="grid grid-cols-1 md:grid-cols-6 gap-5"
        >
          <div className="md:col-span-3 md:row-span-2 bg-slate-900 text-white rounded-3xl p-8 md:p-10 relative overflow-hidden">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-orange-500/20 blur-3xl" />
            <div className="relative flex flex-col h-full">
              <div className="w-14 h-14 rounded-2xl bg-orange-500 text-white flex items-center justify-center mb-5">
                <Clock className="w-7 h-7" />
              </div>
              <div className="font-display text-6xl md:text-7xl font-extrabold tracking-tighter mb-2">
                30min
              </div>
              <div className="text-orange-300 font-bold uppercase tracking-widest text-xs mb-4">
                Intervention Express
              </div>
              <p className="text-slate-300 leading-relaxed max-w-sm">
                Pour tout dépannage, nous nous engageons à intervenir en
                moyenne sous 30 minutes sur un rayon de 30 km autour de
                Mont-Dol.
              </p>
              <div className="mt-auto pt-6">
                <a
                  href={`tel:${COMPANY.phoneDigits}`}
                  data-testid="usp-call-btn"
                  className="inline-flex items-center gap-2 text-amber-400 font-bold hover:text-amber-300"
                >
                  Nous appeler <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          <div className="md:col-span-3 bg-white border border-slate-200 rounded-3xl p-8 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center mb-4">
              <ShieldCheck className="w-6 h-6" />
            </div>
            <h3 className="font-display text-xl md:text-2xl font-bold text-slate-900 mb-2">
              Artisan Qualifié & Diplômé
            </h3>
            <p className="text-slate-600 leading-relaxed">
              Intervention avec soin et professionnalisme en privilégiant
              toujours <strong>l'ouverture fine</strong> lorsque cela est
              possible, pour préserver votre serrure.
            </p>
          </div>

          <div className="md:col-span-3 bg-white border border-slate-200 rounded-3xl p-8 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center mb-4">
              <BadgeEuro className="w-6 h-6" />
            </div>
            <h3 className="font-display text-xl md:text-2xl font-bold text-slate-900 mb-2">
              Tarifs Transparents
            </h3>
            <p className="text-slate-600 leading-relaxed">
              Prix annoncés <strong>avant intervention</strong>, devis gratuit
              et zéro surprise. Remboursement assurance sur facture.
            </p>
          </div>
        </div>
      </section>

      {/* Brand marquee */}
      <BrandMarquee />

      {/* Detailed brands section */}
      <BrandsSection />

      {/* Artisan / about */}
      <ArtisanSection />

      {/* Customer reviews */}
      <Reviews />

      {/* Insurance partners */}
      <InsurancePartners />

      {/* Interventions photos gallery */}
      <InterventionsGallery />

      {/* Services preview */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28">
        <div className="flex items-end justify-between mb-12 gap-6 flex-wrap">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
              <span className="h-px w-8 bg-teal-700" /> Nos services de serrurerie
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Toutes les interventions d'un vrai{" "}
              <span className="text-teal-600">artisan serrurier</span>.
            </h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Avec des centaines d'interventions réalisées en Bretagne, Mon
              Serrurier Breton s'impose comme un acteur local de confiance pour
              les dépannages rapides et les travaux de sécurité.
            </p>
          </div>
          <Link
            data-testid="home-services-link"
            to="/services"
            className="hidden md:inline-flex items-center gap-2 font-bold text-teal-700 hover:text-teal-900"
          >
            Voir tous les services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = ICON_MAP[s.icon] || ShieldCheck;
            return (
              <div
                key={s.id}
                data-testid={`home-service-card-${s.id}`}
                className="group bg-white border border-slate-200 rounded-3xl p-7 hover:border-teal-500 hover:shadow-lg transition-all"
              >
                <div className="flex items-center justify-between mb-5">
                  <div className="w-12 h-12 rounded-2xl bg-teal-50 text-teal-700 flex items-center justify-center group-hover:bg-teal-600 group-hover:text-white transition-colors">
                    <Icon className="w-6 h-6" />
                  </div>
                  <span className="text-slate-300 font-display font-black text-2xl">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-slate-900 mb-2">
                  {s.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed">
                  {s.short}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Mobile team truck section */}
      <section
        data-testid="truck-section"
        className="max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-28"
      >
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-slate-900 to-teal-900 p-8 md:p-14">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-teal-500/20 blur-3xl" />
          <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-[radial-gradient(ellipse_at_bottom,_rgba(44,168,182,0.2),transparent_70%)]" />

          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6 text-white space-y-5">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-teal-300">
                <span className="w-2 h-2 rounded-full bg-teal-400 animate-pulse" />
                Équipe mobile locale
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">
                Un artisan,
                <br />
                <span className="text-teal-300">un atelier sur roues.</span>
              </h2>
              <p className="text-slate-300 leading-relaxed max-w-xl">
                Notre véhicule d'intervention est équipé en permanence de tout
                le matériel professionnel et des pièces de grandes marques
                (Picard, Vachette, Bricard…) nécessaires pour traiter la
                majorité des interventions <strong className="text-white">dès le premier déplacement</strong>.
                Un gain de temps, de budget et de tranquillité pour vous.
              </p>
              <div className="grid grid-cols-3 gap-4 pt-4">
                <div>
                  <div className="font-display text-3xl md:text-4xl font-extrabold text-white">
                    30<span className="text-teal-300">′</span>
                  </div>
                  <div className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">
                    Arrivée moyenne
                  </div>
                </div>
                <div>
                  <div className="font-display text-3xl md:text-4xl font-extrabold text-white">
                    {COMPANY.radiusKm} <span className="text-teal-300">km</span>
                  </div>
                  <div className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">
                    Rayon couvert
                  </div>
                </div>
                <div>
                  <div className="font-display text-3xl md:text-4xl font-extrabold text-white">
                    24<span className="text-teal-300">/7</span>
                  </div>
                  <div className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">
                    Disponibilité
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-6 relative">
              <img
                src={TRUCK_IMAGE}
                alt="Véhicule d'intervention Mon Serrurier Breton"
                data-testid="truck-image"
                className="relative z-10 w-full h-auto object-contain drop-shadow-[0_30px_50px_rgba(0,0,0,0.45)]"
              />
              {/* Ground shadow */}
              <div className="absolute bottom-2 left-4 right-4 h-6 bg-black/50 blur-2xl rounded-full" />
              {/* Floating badge */}
              <div className="absolute -top-2 left-2 md:top-4 md:left-4 z-20 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-2xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-500 text-white flex items-center justify-center font-display font-black">
                  !
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
                    En route
                  </div>
                  <div className="font-display font-extrabold text-slate-900 leading-tight">
                    Vers chez vous
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interventions counter + zone */}
      <section
        data-testid="zone-section"
        className="max-w-7xl mx-auto px-4 md:px-8 py-20"
      >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-teal-700 mb-3">
              <MapPin className="w-4 h-4" /> Zone d'intervention
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-tight">
              Secteur <span className="text-teal-600">22 & 35</span>
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              Grâce à une organisation mobile optimisée, nous pouvons intervenir
              rapidement tout en maintenant des tarifs justes. Notre secteur
              couvre un rayon d'environ{" "}
              <strong>{COMPANY.radiusKm} km autour de Mont-Dol</strong>, où
              nous sommes basés.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-4 max-w-md">
              <div className="rounded-2xl bg-slate-900 text-white p-5">
                <div className="text-xs text-teal-300 font-bold uppercase tracking-widest">
                  Interventions
                </div>
                <div className="font-display text-4xl font-extrabold mt-1">
                  +{COMPANY.interventions}
                </div>
              </div>
              <div className="rounded-2xl bg-teal-600 text-white p-5">
                <div className="text-xs text-teal-100 font-bold uppercase tracking-widest">
                  Rayon
                </div>
                <div className="font-display text-4xl font-extrabold mt-1">
                  {COMPANY.radiusKm} km
                </div>
              </div>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="flex flex-wrap gap-2">
              {INTERVENTION_CITIES.slice(0, 16).map((c) => {
                const slug = c
                  .toLowerCase()
                  .normalize("NFD")
                  .replace(/[\u0300-\u036f]/g, "")
                  .replace(/[^a-z0-9]+/g, "-")
                  .replace(/^-|-$/g, "");
                return (
                  <Link
                    key={c}
                    to={`/serrurier-${slug}`}
                    data-testid={`zone-city-${slug}`}
                    className="inline-flex items-center gap-1.5 bg-white border border-slate-200 hover:border-teal-500 hover:text-teal-700 transition-colors px-4 py-2 rounded-full text-sm font-semibold text-slate-700"
                  >
                    <MapPin className="w-3.5 h-3.5 text-teal-600" />
                    {c}
                  </Link>
                );
              })}
            </div>
            <div className="mt-5 flex items-center justify-between gap-3 flex-wrap">
              <p className="text-xs text-slate-500">
                Votre ville n'est pas listée ? Appelez-nous, nous nous déplaçons
                dans tout le secteur.
              </p>
              <Link
                to="/zones-intervention"
                data-testid="home-see-all-zones"
                className="inline-flex items-center gap-1.5 text-sm font-bold text-teal-700 hover:text-teal-900"
              >
                Voir toutes les communes <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
        <div
          data-testid="final-cta"
          className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-teal-700 via-teal-800 to-slate-900 p-10 md:p-16 text-white"
        >
          <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-amber-400/20 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest mb-4">
                <Phone className="w-3.5 h-3.5" /> Dépannage Ouverture de Porte
                24h/24 — 7j/7
              </div>
              <h2 className="font-display text-3xl md:text-5xl font-extrabold tracking-tight leading-tight">
                Porte claquée ? Clé perdue ?
                <br />
                <span className="text-amber-300">On arrive.</span>
              </h2>
              <p className="text-teal-100 mt-4 max-w-md leading-relaxed">
                Un appel suffit. Intervention en moyenne sous 30 minutes dans
                tout le secteur Mont-Dol / Côte d'Émeraude.
              </p>
            </div>
            <div className="flex md:justify-end">
              <a
                data-testid="final-cta-call"
                href={`tel:${COMPANY.phoneDigits}`}
                className="inline-flex items-center gap-3 bg-orange-500 hover:bg-orange-600 text-white font-bold rounded-full px-8 py-5 shadow-2xl shadow-orange-900/40 text-xl transition-all"
              >
                <Phone className="w-6 h-6" />
                {COMPANY.phone}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
